import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!parallaxRef.current) return;
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      parallaxRef.current.style.transform = `translate(${x}px, ${y}px) scale(1.05)`;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-black">
      {/* Background Image with parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={parallaxRef}
          className="absolute inset-[-5%] transition-transform duration-700 ease-out"
        >
          <img
            src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/e4e0992d344d7af928ebb6731d407351.jpeg"
            alt="The Beast Within"
            className="w-full h-full object-cover object-center"
          />
        </div>
        {/* Dark overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full text-center px-4">
        {/* Pre-title */}
        <div
          className={`transition-all duration-1000 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p
            className="text-[#EADDCC]/60 text-xs tracking-widest-2xl mb-8 uppercase"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.5em' }}
          >
            GUCCI BEAUTY
          </p>
        </div>

        {/* Main Title */}
        <div
          className={`transition-all duration-1200 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <h1
            className="text-[#EADDCC] font-light leading-none mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(52px, 10vw, 140px)',
              letterSpacing: '0.12em',
            }}
          >
            THE BEAST
            <br />
            <em className="italic">WITHIN</em>
          </h1>
        </div>

        {/* Divider */}
        <div
          className={`transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
        >
          <div className="w-24 h-px bg-[#EADDCC]/40 mx-auto mb-6" />
        </div>

        {/* Subtitle */}
        <div
          className={`transition-all duration-1000 delay-900 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p
            className="text-[#EADDCC]/70 text-base md:text-lg font-light italic mb-14"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.15em' }}
          >
            Where elegance meets instinct
          </p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`flex items-center gap-6 transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <Link
            to="/campaign-concept"
            className="group relative px-10 py-3.5 border border-[#EADDCC]/60 text-[#EADDCC] text-xs tracking-widest overflow-hidden cursor-pointer whitespace-nowrap transition-all duration-500 hover:border-[#EADDCC]"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.3em' }}
          >
            <span className="absolute inset-0 bg-[#EADDCC] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            <span className="relative group-hover:text-black transition-colors duration-500">ENTER</span>
          </Link>
          <Link
            to="/visual-development"
            className="text-[#EADDCC]/50 text-xs tracking-widest cursor-pointer hover:text-[#EADDCC] transition-colors duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.3em' }}
          >
            EXPLORE
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-[#EADDCC] text-xs tracking-widest" style={{ letterSpacing: '0.3em', fontSize: '10px' }}>SCROLL</span>
        <div className="w-px h-12 bg-[#EADDCC] animate-pulse" />
      </div>
    </section>
  );
}
